/**
 * Type-safe translation hook
 *
 * Provides access to the current locale's translation function.
 * The returned function accepts translation keys and optional arguments
 * for interpolated strings.
 */

import { useContext } from "react";
import { LanguageContext } from "../contexts/LanguageContext";
import * as translations from "../i18n";
import type { Locale } from "../i18n/translations";

/**
 * Translation function type
 *
 * Accepts a translation key (from the Locale type) and optional
 * interpolation arguments for dynamic translations.
 */
export type TranslateFunction = (
  key: keyof Locale,
  ...args: string[]
) => string;

/**
 * Hook to access the current locale's translation function
 *
 * @returns A typed translation function for the current language
 * @throws Error if used outside of LanguageProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const t = useTranslation();
 *
 *   return (
 *     <div>
 *       <h1>{t("welcome")}</h1>
 *       <p>{t("greeting", userName)}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export const useTranslation = (): TranslateFunction => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useTranslation must be used within a LanguageProvider");
  }

  const { language } = context;

  return translations[language];
};
